import React from "react";
import { ErrorLike } from "../PropX";
import { useEvent } from "./useEvent";

type Param<T> = {
  value: T;
  preflightCheck: (value: T) => boolean;
  validation: (value: T) => ErrorLike | void | Promise<ErrorLike | void>;
};

type Result = {
  validating: boolean;
  message?: string;
  reset(): void;
};

export const useAsyncValidation = <T>(param: Param<T>): Result => {
  const [count, setCount] = React.useState(0);
  const [message, setMessage] = React.useState<string | undefined>(undefined);
  const { validation, preflightCheck, value } = param;

  /**
   * Wrap the validation function in a `useEvent` so that it's reference never changes and
   * we can ignore it for the purposes of the `React.useEffect` below.
   */
  const validationWrapper = useEvent(validation);

  const isMountedRef = React.useRef(true);

  React.useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  /**
   * This effect will run ONLY when the debounced value changes.
   */
  React.useEffect(() => {
    /**
     * If the value is invalid on the client, then we shouldn't bother validating it on the server.
     */
    if (!preflightCheck(value)) return;

    /**
     * Keep track of the most "up to date" validation request using this `isCurrentCheck` flag.
     * If the effect runs again, this will flip to `false` and so any error returned by it will
     * not be assigned.
     */
    let isCurrentCheck = true;

    /**
     * We'll need to increment the count so that we know a check is currently in flight.
     */
    setCount((prev) => prev + 1);

    const exec = async () => {
      try {
        /**
         * Assign the result of the validation to a variable so that we can check the type of it.
         * If it's a promise, then we need to wait for it to resolve and indicate that we're validating the debouncedValue.
         */

        const err = await validationWrapper(value);

        if (err !== undefined && isCurrentCheck) {
          setMessage(err.message);
        }
      } catch {
        /**
         * If the validation function throws for whatever reason, we catch the error and call `error` to make sure that
         * everything is unblocked.
         */
        if (isCurrentCheck) {
          setMessage("An unexpected error occurred.");
        }
      } finally {
        /**
         * If the `isMounted` callback was called, then we know that the component was unmounted
         * and we shouldn't attempt to update the count because it doesn't matter.
         */
        if (isMountedRef.current) {
          /**
           * Make sure to decrement the count so that we know a check is no longer in flight.
           */
          setCount((prev) => prev - 1);
        }
      }
    };

    exec();

    return () => {
      isCurrentCheck = false;
    };
  }, [value]);

  return React.useMemo(() => {
    return {
      message,
      validating: count > 0,
      reset: () => {
        if (message !== undefined) setMessage(undefined);
      },
    };
  }, [count > 0, message]);
};
